import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, uid } from "@/lib/api";
import { ErrorBox, Field, Modal, Sel } from "@/components/common";

export const WD_TYPES = { company_expense: "مصروف للمؤسسة", custody: "عهدة", personal: "سحب شخصي" };
const isShort = (e) => /غير كافٍ/.test(errMsg(e));

// Generic dialog: fields = [[key, label, type, options]]; send(values, override) posts. Handles negative-balance override.
export function FinDialog({ open, onOpenChange, title, fields, initial, send, testid, done }) {
  const { can } = useAuth();
  const [f, setF] = useState(initial);
  const [key] = useState(uid());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [short, setShort] = useState(false);
  const [ovr, setOvr] = useState("");
  const go = async () => {
    setBusy(true); setErr("");
    try { await send(f, key, short ? ovr : null); toast.success("تم الحفظ"); onOpenChange(false); done?.(); }
    catch (e) { setErr(errMsg(e)); if (isShort(e) && can("approve_finance")) setShort(true); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title}
      footer={<button className="k-btn-primary" disabled={busy || (short && ovr.trim().length < 3)} onClick={go} data-testid={`${testid}-submit`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} {short ? "تأكيد مع تجاوز الرصيد" : "حفظ"}</button>}>
      <div className="space-y-3" data-testid={testid}>
        {fields.filter(Boolean).map(([k, label, type, opts]) => (
          <Field key={k} label={label}>
            {type === "select" ? <Sel value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`${testid}-${k}`}>{opts}</Sel>
              : <input className={`k-input ${type === "number" ? "num text-right" : ""}`} inputMode={type === "number" ? "numeric" : undefined} value={f[k] ?? ""}
                onChange={(e) => setF({ ...f, [k]: type === "number" ? e.target.value.replace(/\D/g, "") : e.target.value })} data-testid={`${testid}-${k}`} />}
          </Field>
        ))}
        <ErrorBox text={err} />
        {short && <Field label="مبرر تجاوز الرصيد (يُسجل في سجل التدقيق) *"><textarea className="k-input border-amber-400" value={ovr} onChange={(e) => setOvr(e.target.value)} data-testid={`${testid}-override`} /></Field>}
      </div>
    </Modal>
  );
}

const methods = (s) => (s?.payment_methods || ["نقدًا"]).map((m) => <option key={m}>{m}</option>);

export function MoveDialog({ t, kind, onOpenChange, done }) {
  const { settings } = useAuth();
  const wd = kind === "withdrawal";
  return <FinDialog open onOpenChange={onOpenChange} done={done} testid="move-form" title={`${wd ? "سحب / مخرج" : "مدخول آخر"} – ${t.name}`}
    initial={{ amount: "", method: settings?.payment_methods?.[0] || "نقدًا", withdrawal_type: "company_expense", category: "", reason: "", beneficiary: "", reference: "" }}
    fields={[["amount", "المبلغ (دج) *", "number"], wd && ["withdrawal_type", "تصنيف السحب *", "select", Object.entries(WD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)],
      ["category", "التصنيف"], ["reason", "السبب *"], ["beneficiary", wd ? "المستفيد" : "المصدر"], ["method", "طريقة الدفع", "select", methods(settings)], ["reference", "المرجع"]]}
    send={(f, key, ovr) => api.post("/treasury-moves", { ...f, treasury_id: t.id, kind, amount: Number(f.amount), idempotency_key: key, override_reason: ovr,
      category: f.category || null, beneficiary: f.beneficiary || null, reference: f.reference || null, withdrawal_type: wd ? f.withdrawal_type : null })} />;
}

export function TransferDialog({ t, targets, onOpenChange, done }) {
  const { settings } = useAuth();
  const main = targets.find((x) => x.kind === "main" && x.id !== t.id);
  return <FinDialog open onOpenChange={onOpenChange} done={done} testid="transfer-form" title={`تحويل من ${t.name} (الرصيد ${money(t.balance)})`}
    initial={{ to_id: main?.id || "", amount: "", method: settings?.payment_methods?.[0] || "نقدًا", receiver_name: "", reference: "", notes: "" }}
    fields={[["to_id", "إلى الخزينة *", "select", [<option key="" value="">— اختر —</option>, ...targets.filter((x) => x.id !== t.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)]],
      ["amount", "المبلغ (دج) *", "number"], ["receiver_name", "اسم المستلم"], ["method", "الطريقة", "select", methods(settings)], ["reference", "المرجع"], ["notes", "ملاحظات"]]}
    send={(f, key, ovr) => api.post("/transfers", { ...f, from_id: t.id, amount: Number(f.amount), idempotency_key: key, override_reason: ovr,
      receiver_name: f.receiver_name || null, reference: f.reference || null, notes: f.notes || null })} />;
}

export function CountDialog({ t, onOpenChange, done }) {
  return <FinDialog open onOpenChange={onOpenChange} done={done} testid="count-form" title={`جرد ${t.name} – الرصيد النظري ${money(t.balance)}`}
    initial={{ counted: "", notes: "" }} fields={[["counted", "النقد الفعلي المعدود (دج) *", "number"], ["notes", "ملاحظات"]]}
    send={(f) => api.post(`/treasuries/${t.id}/counts`, { counted: Number(f.counted || 0), notes: f.notes || null })} />;
}

export function CreateTreasuryDialog({ users, onOpenChange, done }) {
  return <FinDialog open onOpenChange={onOpenChange} done={done} testid="treasury-form" title="خزينة جديدة"
    initial={{ name: "", user_id: "", opening_balance: "" }}
    fields={[["name", "اسم الخزينة *"], ["user_id", "المستخدم المسؤول", "select", [<option key="" value="">— خزينة عامة بدون مستخدم —</option>, ...users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)]],
      ["opening_balance", "الرصيد الافتتاحي (دج)", "number"]]}
    send={(f) => api.post("/treasuries", { name: f.name, user_id: f.user_id || null, opening_balance: Number(f.opening_balance || 0) })} />;
}
